"use client"

import { Link } from "@i18n/navigation"
import { useLocale } from "next-intl"

import { DownloadIcon } from "@/components/ui/download"
import { RocketIcon } from "@/components/ui/rocket"
import { HyperText } from "@/components/ui/hyper-text"
import { RainbowButton } from "@/components/ui/rainbow-button"

type HeroClientProps = {
  title: string
  tagline: string
  /** Rainbow CTA label ("立即开始" / "Get Started"); links to /#pricing. */
  primaryCta: string
  /** Black download button label + the "免费" suffix that fades in on hover. */
  downloadLabel: string
  downloadSuffix: string
  /** The installer address; always set, since env.ts defaults it to the release page. */
  downloadUrl?: string
  /** 后台传上来的首屏图（Media URL）。为空则用 locale 对应的内置截图。 */
  imageUrl?: string | null
}

/**
 * Client half of the hero: HyperText animates after mount and the download
 * button relies on group-hover, so both live behind this boundary. Copy is
 * passed in translated from the server component.
 */
const HeroClient = ({
  title,
  tagline,
  primaryCta,
  downloadLabel,
  downloadSuffix,
  downloadUrl,
  imageUrl,
}: HeroClientProps) => {
  const locale = useLocale()
  // 后台没传图时回落到内置截图（中英各一张）。
  const screenshot = imageUrl ?? (locale === "zh" ? "/images/hero-zh.png?v=2" : "/images/hero-en.png?v=2")

  return (
    <section className="border-b border-ui-border-base">
      <div className="content-container flex flex-col items-center gap-y-6 pt-20 pb-10 text-center lg:pt-28 lg:pb-12">
        <HyperText
          as="h1"
          duration={1800}
          className="max-w-3xl text-4xl sm:text-5xl"
          data-testid="hero-title"
        >
          {title}
        </HyperText>
        <p className="text-base-regular max-w-xl text-ui-fg-subtle">
          {tagline}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-3 pt-2">
          <RainbowButton
            asChild
            size="lg"
            className="min-w-44"
            data-testid="hero-primary-cta"
          >
            <Link href="/#pricing">
              <RocketIcon size={18} className="shrink-0" />
              {primaryCta}
            </Link>
          </RainbowButton>
          {downloadUrl ? (
            /* Tailwind port of the styled-components download button: black
               plate, dark shadow; on hover the label slides right and the
               suffix fades in from the left edge of its slot. Fixed min-width
               keeps the reveal from shifting layout. */
            <a
              href={downloadUrl}
              data-testid="hero-download-cta"
              className="group inline-flex h-11 min-w-44 items-center justify-center gap-x-2 rounded-xl bg-black px-6 text-sm font-medium text-white shadow-[3px_3px_10px_rgba(0,0,0,0.9),-1px_-1px_6px_rgba(255,255,255,0.1)] ring-1 ring-white/10 transition-shadow duration-300 hover:shadow-[3px_3px_14px_rgba(0,0,0,1),-1px_-1px_6px_rgba(255,255,255,0.15)]"
            >
              <DownloadIcon size={18} className="shrink-0" />
              <span className="transition-transform duration-300 ease-out group-hover:translate-x-1 group-hover:tracking-wider">
                {downloadLabel}
              </span>
              <span className="-translate-x-1 text-white/60 opacity-0 transition-all duration-300 ease-out group-hover:translate-x-0 group-hover:opacity-100">
                {downloadSuffix}
              </span>
            </a>
          ) : null}
        </div>
        {/* Locale-matched UI shot: zh keeps the Chinese screenshot, every
            other locale gets the English one. 2026-11-02: 后台可以传图顶掉它
            （`imageUrl`），为空才用内置那张。

            The `?v=` suffix is a cache key, not a real query: /images/* is
            cached for hours by the CDN and by the browser (Cache-Control
            max-age=14400 measured on production), so replacing the file
            without bumping it keeps showing the previous shot. Bump it
            whenever either PNG is re-exported (2026-10-21). */}
        <div className="w-full max-w-4xl overflow-hidden rounded-xl border border-ui-border-base shadow-2xl">
          <img
            src={screenshot}
            alt=""
            data-testid="hero-app-screenshot"
            className="h-auto w-full"
          />
        </div>
      </div>
    </section>
  )
}

export default HeroClient
